import React, { useState, useEffect, useRef } from "react";

import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import noImage from "../../../assets/images/no-image-found.jpeg";
import { useSelector } from "react-redux";
import { fetchDocument } from "../../../helpers/api/document";
import Spinner from "../../../components/Spinner";

const ItemImage = (props) => {
  const documentId = props.documentId;
  const [imageLoading, setImageLoading] = useState(false);
  const imageClass = props.rounded ? "rounded-circle" : "square";
  const zoomClass = props.zoom ? "image-container" : "";
  const [isHovered, setIsHovered] = useState(false);
  const [documentURL, setDocumentURL] = useState(props.documentURL);
  const lastForcedFetchKeyRef = useRef(null);
  const showSpinner =
    props.showSpinner !== undefined ? props.showSpinner : false;
  const { user } = useSelector((state) => ({
    user: state.Auth.user,
  }));
  const defaultStyle = {
    transition: "transform 0.3s",
    zIndex: "1",
  };

  const hoverStyle = props.scaleOnHover
    ? {
        transform: isHovered ? "scale(1.5)" : "scale(1)",
        zIndex: isHovered ? "2" : "1",
        marginLeft: "-33px",
        position: "relative",
        left: "33px",
      }
    : {};

  const shouldForceFetchThumbnail = Boolean(props.thumbnailSize);
  const forcedFetchKey = `${documentId || ""}|${props.thumbnailSize || ""}|${props.thumbnailFit || ""}|${props.recordType || ""}|${props.supplierRequestItemId || ""}|${user.country || ""}`;

  const getDocumentAWS = async ({ documentId }) =>
    fetchDocument(documentId, {
      recordType: props.recordType,
      thumbnailSize: props.thumbnailSize,
      thumbnailFit: props.thumbnailFit,
      supplierRequestItemId: props.supplierRequestItemId,
    });
  useEffect(() => {
    if (
      shouldForceFetchThumbnail &&
      lastForcedFetchKeyRef.current === forcedFetchKey &&
      documentURL
    ) {
      setImageLoading(false);
      props.imageLoading && props.imageLoading(false);
      return;
    }

    if (props.documentURL && !shouldForceFetchThumbnail) {
      setDocumentURL(props.documentURL);
      setImageLoading(false);
      props.imageLoading && props.imageLoading(false);
      return;
    }

    if (documentId) {
      setImageLoading(true);
      props.imageLoading && props.imageLoading(true);
      if (shouldForceFetchThumbnail) {
        lastForcedFetchKeyRef.current = forcedFetchKey;
      }

      getDocumentAWS({
        documentId: documentId,
      })
        .then((response) => {
          if (user.country === "CN") {
            if (response.status === 200) {
              // Lambda returns base64; the API Gateway doesn't reliably decode
              // for this setup, so decode in JS and rebuild the blob with the
              // real Content-Type (from X-Content-Type) so <img> renders.
              response.text().then((text) => {
                const realType = response.headers.get("X-Content-Type");
                const binary = atob(text);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                const blob = new Blob([bytes], realType ? { type: realType } : {});
                const fetchedURL = window.URL.createObjectURL(blob);
                props.updateDocumentHandler &&
                  props.updateDocumentHandler(fetchedURL);
                setDocumentURL(fetchedURL);
                setImageLoading(false);
                props.imageLoading && props.imageLoading(false);
              });
            } else {
              // Fehler oder kein Bild: Wir setzen den Sentinel-Wert
              props.updateDocumentHandler &&
                props.updateDocumentHandler("no-image");
              setDocumentURL("no-image");
              setImageLoading(false);
              props.imageLoading && props.imageLoading(false);
            }
          } else {
            response.json().then((documentsData) => {
              const fetchedURL = documentsData.downloadURL || "no-image";
              props.updateDocumentHandler &&
                props.updateDocumentHandler(fetchedURL);
              setDocumentURL(fetchedURL);
              setImageLoading(false);
              props.imageLoading && props.imageLoading(false);
            });
          }
        })
        .catch((error) => {
          console.log("ERROR: ", error);
          // Bei einem Netzwerkfehler setzen wir ebenfalls "no-image", um Loops zu verhindern
          props.updateDocumentHandler &&
            props.updateDocumentHandler("no-image");
          setDocumentURL("no-image");
          setImageLoading(false);
          props.imageLoading && props.imageLoading(false);
        });
    } else {
      setDocumentURL(null);
      setImageLoading(false);
      props.imageLoading && props.imageLoading(false);
    }
  }, [
    documentId,
    props.documentURL,
    props.thumbnailSize,
    props.thumbnailFit,
    props.recordType,
    props.supplierRequestItemId,
    forcedFetchKey,
    shouldForceFetchThumbnail,
    user.country,
  ]);

  // Hilfsfunktion um die korrekte URL zu ermitteln (beachtet den Sentinel-Wert)
  const getFinalImageURL = () => {
    const url = shouldForceFetchThumbnail
      ? documentURL
      : props.documentURL || documentURL;
    if (!url || url === "no-image") return noImage;
    return url;
  };

  return (
    <React.Fragment>
      {showSpinner && imageLoading ? (
        <div
          style={{
            width: props.width ? `${props.width}px` : "32px",
            height: props.height ? `${props.height}px` : "32px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner
            className="spinner-border-sm"
            tag="span"
            color="lineup-red"
          />
        </div>
      ) : imageLoading ? (
        <img
          src={noImage}
          alt=""
          className={`rounded ${imageClass} ${zoomClass} ${props.className ? props.className : ""}`}
          width={props.width ? props.width : "32"}
          height={props.height ? props.height : "32"}
          style={{ ...defaultStyle, ...(props.scaleOnHover && hoverStyle), ...props.style }}
        />
      ) : (
        <LazyLoadImage
          visibleByDefault={true}
          placeholderSrc={noImage}
          src={getFinalImageURL()}
          alt=""
          className={`${props.rounded ? "rounded-circle" : "rounded"} ${
            props.className
          } ${imageClass} ${zoomClass} ${
            props.className ? props.className : ""
          }`}
          width={props.width ? props.width : "32"}
          height={props.height ? props.height : "32"}
          documentURL={props.documentId && props.documentURL}
          style={{
            ...defaultStyle,
            ...(props.scaleOnHover && hoverStyle),
            ...props.style,
          }}
          onMouseEnter={() => props.scaleOnHover && setIsHovered(true)}
          onMouseLeave={() => props.scaleOnHover && setIsHovered(false)}
        />
      )}
    </React.Fragment>
  );
};

export default React.memo(ItemImage);
